import { State } from '@meraki-internal/state';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { UserState } from '../user/UserState';
import { CareMapState } from '../caremap/CareMapState';
import { UserOnboardingState } from '../onboarding/UserOnboardingState';
import { GetHelpPromptStorage } from './GetHelpPromptStorage';
import { buildGetHelpSurveyUrl } from './getHelpSurveyUrl';

export class GetHelpPromptViewModel extends State<Record<string, never>> {
    private _isOpen = false;
    private _showCount = 0;
    private promptCheckScheduled = false;

    static inject = () => [
        GetHelpPromptStorage,
        MixPanelEventEmitter,
        UserState,
        CareMapState,
        UserOnboardingState
    ];

    constructor(
        private storage: GetHelpPromptStorage,
        private mixpanel: MixPanelEventEmitter,
        private userState: UserState,
        private careMapState: CareMapState,
        private userOnboardingState: UserOnboardingState
    ) {
        super({});

        // When onboarding state changes, check if we should schedule the popup
        this.userOnboardingState.subscribe(() => {
            if (this.userOnboardingState.hasCompletedWelcomeSlides()) {
                this.schedulePromptCheck();
            }
        });
    }

    private schedulePromptCheck = () => {
        if (this.promptCheckScheduled) return;
        this.promptCheckScheduled = true;

        setTimeout(() => {
            this.checkAndShowPrompt();
        }, 5000);
    };

    isOpen = () => this._isOpen;
    getShowCount = () => this._showCount;

    checkAndShowPrompt = async () => {
        // Don't show if Welcome slides haven't been completed
        if (!this.userOnboardingState.hasCompletedWelcomeSlides()) {
            return;
        }

        const shouldShow = await this.storage.shouldShowPrompt();
        if (shouldShow) {
            await this.storage.markPromptShown();
            this._showCount = await this.storage.getShowCount();
            this._isOpen = true;
            this.setState({});
        }
    };

    private popupMessage = {
        title: 'Want a personal guide to find the right services for you?',
        description: 'We\'ll build you a plan with recommended resources!'
    };

    close = async () => {
        this._isOpen = false;
        this.setState({});

        const showCount = await this.storage.getShowCount();
        this.mixpanel.track('Get Help Prompt Dismissed', () => ({
            showCount,
            message: this.popupMessage
        }));
    };

    acceptAndOpenSurvey = async () => {
        await this.storage.markPromptAccepted();
        this._isOpen = false;
        this.setState({});

        this.mixpanel.track('Get Help Experiment Opened', () => ({
            source: 'popup',
            message: this.popupMessage
        }));

        const caremap = this.careMapState.getMostRecentCaremap();
        const surveyUrl = buildGetHelpSurveyUrl({
            email: this.userState.state.email,
            firstName: this.userState.state.firstName,
            lastName: this.userState.state.lastName,
            caremap,
            source: 'popup'
        });

        window.open(surveyUrl, '_blank');
    };
}
